import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useMatch, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth';
import { HiveMark } from './HiveMark';
import { FolderIcon, HomeIcon, InboxIcon, MenuIcon, PeopleIcon, CloseIcon } from './icons';
import { FOCUS_RING } from './ui';

const DRAWER_ID = 'sidebar-drawer';

export function Layout({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  const drawerTriggerRef = useRef<HTMLButtonElement>(null);

  // Picking a link inside the drawer navigates, but the drawer would otherwise
  // stay open on top of the page it just opened.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;

    // Focus moves into the drawer on open and back to the hamburger on close --
    // otherwise the caret is left on a button that is now underneath a scrim,
    // and the first Tab walks the page behind it.
    drawerCloseRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setDrawerOpen(false);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      drawerTriggerRef.current?.focus();
    };
  }, [drawerOpen]);

  return (
    <div className="flex h-full">
      <aside className="hidden w-60 shrink-0 border-r border-stone-200 bg-white md:flex">
        <SidebarContent />
      </aside>

      {/*
        The drawer stays mounted and slides, rather than being conditionally
        rendered. Mounting it on open is why it used to appear instantly while
        the task panel two directories away animates: there is no exit frame to
        animate *from* once the element is already gone.

        `inert` while closed keeps it out of the tab order and off the
        accessibility tree, which is the part a translate alone does not do.
      */}
      <div
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-stone-900/40 transition-opacity duration-200 motion-reduce:transition-none md:hidden ${
          drawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <aside
        id={DRAWER_ID}
        inert={!drawerOpen}
        className={`fixed inset-y-0 left-0 z-50 flex w-60 border-r border-stone-200 bg-white shadow-xl transition-transform duration-200 motion-reduce:transition-none md:hidden ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent closeRef={drawerCloseRef} onClose={() => setDrawerOpen(false)} />
      </aside>

      {/* `min-w-0` so page grids and flex rows shrink inside the column rather
          than pushing it wider than the viewport. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-stone-200 bg-white px-4 py-3 md:hidden">
          <button
            ref={drawerTriggerRef}
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
            aria-controls={DRAWER_ID}
            className={`-m-1 rounded-lg p-2 text-stone-700 transition hover:bg-stone-100 ${FOCUS_RING}`}
          >
            <MenuIcon />
          </button>
          <span className="flex items-center gap-2">
            <HiveMark className="h-5 w-5 text-honey-500" />
            <span className="font-semibold tracking-tight">Hive</span>
          </span>
        </header>

        {/*
          The scroll container is the element; the width cap is the child.
          A page used to run the full width of the display, so on anything wider
          than a laptop the task table stretched until its columns had nothing
          to do with each other. The cap is deliberately generous -- this is a
          table-shaped app, not an article -- and `2xl:` only, so nothing below a
          very wide screen changes at all.

          Scrolling is frozen while the drawer is open: it is `fixed`, so without
          this the page slides around underneath it on a touch screen.
        */}
        <main className={`flex-1 ${drawerOpen ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <div className="mx-auto w-full max-w-[100rem] px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

/** Shared by the pinned desktop sidebar and the mobile drawer. */
function SidebarContent({
  onClose,
  closeRef,
}: {
  onClose?: () => void;
  closeRef?: React.Ref<HTMLButtonElement>;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex w-full flex-col px-3 py-4">
      <div className="flex items-center justify-between">
        <Link
          to="/"
          aria-label="Hive dashboard"
          className={`flex items-center gap-2 rounded-lg px-2 py-1 transition hover:opacity-80 ${FOCUS_RING}`}
        >
          <HiveMark className="h-6 w-6 text-honey-500" />
          <span className="text-lg font-semibold tracking-tight">Hive</span>
        </Link>

        {onClose ? (
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className={`rounded-lg p-1.5 text-stone-700 transition hover:bg-stone-100 ${FOCUS_RING}`}
          >
            <CloseIcon />
          </button>
        ) : null}
      </div>

      <nav className="mt-6 flex flex-col gap-0.5 text-sm">
        <NavItem to="/" icon={<HomeIcon />}>
          Dashboard
        </NavItem>
        <NavItem to="/tasks" icon={<InboxIcon />}>
          Tasks
        </NavItem>
        <NavItem to="/projects" icon={<FolderIcon />}>
          Projects
        </NavItem>
        <NavItem to="/assignees" icon={<PeopleIcon />}>
          Assignees
        </NavItem>
      </nav>

      <div className="mt-auto border-t border-stone-200 pt-3 text-sm">
        <div className="flex items-center gap-2 px-2">
          {user?.pictureUrl ? (
            <img
              src={user.pictureUrl}
              alt=""
              className="h-7 w-7 shrink-0 rounded-full ring-1 ring-stone-200"
              referrerPolicy="no-referrer"
            />
          ) : null}
          {/* `min-w-0` + `truncate`: a long Google display name would otherwise
              stretch the fixed-width sidebar. */}
          <span className="min-w-0 truncate text-stone-500">{user?.name ?? user?.email}</span>
        </div>
        <button
          type="button"
          onClick={() => void handleLogout()}
          className={`mt-2 w-full rounded-lg px-3 py-2 text-left font-medium text-stone-600 transition hover:bg-stone-100 hover:text-stone-900 ${FOCUS_RING}`}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * The active item is the app's loudest use of the accent, and that is on
 * purpose: "where am I" is the one question the chrome exists to answer, and it
 * used to be answered with `bg-slate-100` -- one step from the `bg-slate-50`
 * hover on the item directly below it.
 *
 * The rail is a pseudo-element rather than a border so the label does not shift
 * by two pixels as you move between items.
 */
function NavItem({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) {
  /**
   * The dashboard's task panel is `/task/:id`, a sibling path rather than
   * something under `/`, so `end` below -- which is what stops every other page
   * lighting the Dashboard item up -- also unlights it while the panel is open.
   * Reading the panel's own path back is the narrow exception that keeps the
   * one question this rail answers answered: the panel is still the dashboard.
   */
  const onDashboardPanel = useMatch('/task/:taskId') !== null;

  return (
    <NavLink
      to={to}
      // `end` so /projects does not leave the Dashboard link highlighted too.
      end={to === '/'}
      className={({ isActive }) =>
        `relative flex items-center gap-3 rounded-lg px-3 py-2 font-medium transition ${FOCUS_RING} ${
          isActive || (to === '/' && onDashboardPanel)
            ? 'bg-honey-50 text-honey-900 before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-honey-500'
            : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
        }`
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}
